import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import type { SeriesResponse } from '../../services/analyticsService'
import { DIMENSIONS, DIMENSION_LABELS, type ChartMetric, type Dimension } from '../../utils/exploreFilters'

// Colours: slots 1-3 (dark steps) of the dataviz skill's reference categorical
// palette, plus a de-emphasis gray for "other". Checked with its
// validate_palette.js in dark mode against this card's surface (#111113 to
// #151517), all pairs, since a zero-height segment can make any two touch:
// worst CVD ΔE 9.4, worst normal-vision ΔE 16.6, every mark >= 3:1. The
// palette's usual muted gray (#898781) measured deutan ΔE 2.0 against the
// aqua slot -- indistinguishable -- hence the darker gray.
const SERIES_COLORS = ['#3987e5', '#d95926', '#199e70'] as const
const OTHER_COLOR = '#686763'
const GRID_COLOR = '#2c2c2a'
const BASELINE_COLOR = '#383835'
const MUTED_INK = '#898781'

const PLOT_HEIGHT = 180
const TOP_PAD = 18 // room for the peak label above the tallest column
const AXIS_BAND = 24 // the x-axis labels live inside the SVG height, never clipped
const LEFT_GUTTER = 44
const RIGHT_PAD = 8
const GAP = 2 // surface gap between touching marks
const MAX_BAR = 24
const RADIUS = 4

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

interface Layer {
  key: string
  label: string
  color: string
  values: number[]
}

interface ActivityChartProps {
  data: SeriesResponse | undefined
  isLoading: boolean
  /** A previous render is on screen while newer data loads. */
  isRefetching: boolean
  isError: boolean
  metric: ChartMetric
  onMetricChange: (metric: ChartMetric) => void
  splitBy: Dimension | ''
  onSplitByChange: (dimension: Dimension | '') => void
  formatValue: (dimension: Dimension, value: string) => string
}

// Bucket labels arrive as local wall-clock strings ("2026-09-18" or
// "2026-09-18T14:00"). They are split as text, never parsed into a Date, so
// no timezone conversion can move a point into the wrong bucket.
function parseBucket(label: string) {
  const [date, time] = label.split('T')
  const [, month, day] = date.split('-')
  return { month: MONTHS[Number(month) - 1] ?? month, day: String(Number(day)), year: date.slice(0, 4), hour: time?.slice(0, 5) }
}

function axisLabel(label: string, isFirst: boolean): string {
  const { month, day, hour } = parseBucket(label)
  if (!hour) return `${month} ${day}`
  return isFirst || hour === '00:00' ? `${month} ${day}` : hour
}

const MIN_LABEL_SPACING = 72
const HOUR_STEPS = [1, 2, 3, 4, 6, 12, 24]
const DAY_STEPS = [1, 2, 3, 7, 14]

/**
 * Which buckets get an x-axis label. Hourly labels step by a whole number of
 * hours aligned to the local clock (00:00, 06:00, 12:00...) so midnight is
 * always labelled -- and carries the date. Stepping by an arbitrary count
 * instead lands on 05:00, 16:00, 03:00... and a multi-day chart loses its
 * dates entirely.
 */
function labelledIndices(buckets: string[], unit: 'hour' | 'day' | undefined, plotWidth: number): Set<number> {
  const maxLabels = Math.max(2, Math.floor(plotWidth / MIN_LABEL_SPACING))
  const indices = new Set<number>()
  if (unit === 'hour') {
    let step = HOUR_STEPS.find(candidate => buckets.length / candidate <= maxLabels)
    // Past one label a day: label every Nth midnight.
    const dayStride = step ? 1 : Math.ceil(buckets.length / 24 / maxLabels)
    step = step ?? 24
    let midnights = 0
    buckets.forEach((bucket, index) => {
      const hour = Number(parseBucket(bucket).hour?.slice(0, 2))
      if (hour % step !== 0) return
      if (step === 24 && midnights++ % dayStride !== 0) return
      indices.add(index)
    })
  } else {
    const step = DAY_STEPS.find(candidate => buckets.length / candidate <= maxLabels) ?? Math.ceil(buckets.length / maxLabels)
    buckets.forEach((_, index) => {
      if (index % step === 0) indices.add(index)
    })
  }
  // Never leave a chart with no labels at all.
  if (indices.size === 0 && buckets.length > 0) indices.add(0)
  return indices
}

function tooltipTitle(label: string): string {
  const { month, day, year, hour } = parseBucket(label)
  if (!hour) return `${month} ${day}, ${year}`
  const next = `${String((Number(hour.slice(0, 2)) + 1) % 24).padStart(2, '0')}:00`
  return `${month} ${day}, ${hour}–${next}`
}

function niceStep(rough: number): number {
  if (rough <= 1) return 1
  const exponent = 10 ** Math.floor(Math.log10(rough))
  const fraction = rough / exponent
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10
  return nice * exponent
}

/**
 * Colour follows the value, never its rank. Top values reorder as filters
 * change; assigning by position would repaint "ios" from blue to orange when
 * the range widens. Values keep their slot for as long as the page is open;
 * a newcomer takes the lowest free slot.
 */
function useStableColors(splitKey: string, values: string[]): Map<string, string> {
  const memory = useRef(new Map<string, Map<string, number>>())
  const signature = values.join('\u0000')
  return useMemo(() => {
    let slots = memory.current.get(splitKey)
    if (!slots) {
      slots = new Map()
      memory.current.set(splitKey, slots)
    }
    const taken = new Set<number>()
    const colors = new Map<string, string>()
    for (const value of values) {
      const slot = slots.get(value)
      if (slot !== undefined && !taken.has(slot)) {
        taken.add(slot)
        colors.set(value, SERIES_COLORS[slot])
      }
    }
    for (const value of values) {
      if (colors.has(value)) continue
      const slot = SERIES_COLORS.findIndex((_, index) => !taken.has(index))
      if (slot === -1) continue // the API returns at most three named values
      taken.add(slot)
      slots.set(value, slot)
      colors.set(value, SERIES_COLORS[slot])
    }
    return colors
    // `values` is represented by `signature`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitKey, signature])
}

/** Top corners rounded, square at the baseline. */
function roundedTopPath(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.max(0, Math.min(radius, width / 2, height))
  return `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`
}

export default function ActivityChart({
  data,
  isLoading,
  isRefetching,
  isError,
  metric,
  onMetricChange,
  splitBy,
  onSplitByChange,
  formatValue,
}: ActivityChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [active, setActive] = useState<number | null>(null)
  const [showTable, setShowTable] = useState(false)

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width))
    observer.observe(element)
    return () => observer.disconnect()
  }, [showTable])

  const isSplit = metric === 'events' && Boolean(data && data.series.length > 0)
  const namedValues = useMemo(
    () => (isSplit ? (data?.series ?? []).filter(line => !line.isOther).map(line => line.value as string) : []),
    [isSplit, data],
  )
  const colors = useStableColors(splitBy, namedValues)

  const layers: Layer[] = useMemo(() => {
    if (!data) return []
    if (!isSplit) {
      return [{ key: 'total', label: metric === 'events' ? 'Events' : 'Sessions', color: SERIES_COLORS[0], values: data.total[metric] }]
    }
    // Bottom to top in the API's order (most events first), "other" last.
    return data.series.map(line => ({
      key: line.isOther ? '\u0000other' : (line.value as string),
      label: line.isOther ? 'Other' : formatValue(splitBy as Dimension, line.value as string),
      color: line.isOther ? OTHER_COLOR : colors.get(line.value as string) ?? OTHER_COLOR,
      values: line.events,
    }))
  }, [data, isSplit, metric, splitBy, colors, formatValue])

  const buckets = useMemo(() => data?.buckets ?? [], [data])
  const totals = useMemo(
    () => buckets.map((_, index) => layers.reduce((sum, layer) => sum + (layer.values[index] ?? 0), 0)),
    [buckets, layers],
  )
  const dataMax = Math.max(0, ...totals)
  const step = niceStep(dataMax / 4)
  const yMax = Math.max(step, Math.ceil(dataMax / step) * step)
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, index) => index * step)

  const plotWidth = Math.max(0, width - LEFT_GUTTER - RIGHT_PAD)
  const band = buckets.length > 0 ? plotWidth / buckets.length : 0
  const barWidth = Math.max(1, Math.min(MAX_BAR, band - GAP))
  const svgHeight = TOP_PAD + PLOT_HEIGHT + AXIS_BAND
  const baselineY = TOP_PAD + PLOT_HEIGHT
  const scale = (value: number) => (value / yMax) * PLOT_HEIGHT
  const labelled = useMemo(() => labelledIndices(buckets, data?.unit, plotWidth), [buckets, data?.unit, plotWidth])
  const firstLabelled = Math.min(...labelled)
  const peakIndex = totals.indexOf(dataMax)
  const isEmpty = !isLoading && data !== undefined && dataMax === 0

  const onKeyDown = (event: KeyboardEvent) => {
    if (buckets.length === 0) return
    const last = buckets.length - 1
    const current = active ?? last
    const next =
      event.key === 'ArrowLeft' ? Math.max(0, current - 1)
      : event.key === 'ArrowRight' ? Math.min(last, current + 1)
      : event.key === 'Home' ? 0
      : event.key === 'End' ? last
      : null
    if (next === null) return
    event.preventDefault()
    setActive(next)
  }

  const metricLabel = metric === 'events' ? 'Events' : 'Sessions'
  const caption = isSplit
    ? `Events by ${DIMENSION_LABELS[splitBy as Dimension].toLowerCase()}, per ${data?.unit ?? 'bucket'}`
    : `${metricLabel} per ${data?.unit ?? 'bucket'}`

  return (
    <div className="border-b border-white/10 px-5 py-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Activity</h3>
          <p className="mt-1 text-xs text-gray-500">
            {caption}
            {data && ` · ${data.timezone}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-white/10 bg-white/[0.035] p-0.5" role="group" aria-label="Metric">
            {(['events', 'sessions'] as const).map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={metric === option}
                onClick={() => onMetricChange(option)}
                className={`rounded-lg px-3 py-1 text-xs font-semibold ${metric === option ? 'bg-white text-gray-950' : 'text-gray-400 hover:text-white'}`}
              >
                {option === 'events' ? 'Events' : 'Sessions'}
              </button>
            ))}
          </div>
          <select
            aria-label="Split by"
            value={metric === 'events' ? splitBy : ''}
            disabled={metric === 'sessions'}
            title={metric === 'sessions' ? 'Sessions cannot be split: one session can belong to several values.' : undefined}
            onChange={event => onSplitByChange(event.target.value as Dimension | '')}
            className="rounded-xl border border-white/10 bg-white/[0.035] px-3 py-1.5 text-xs text-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <option value="">No split</option>
            {DIMENSIONS.map(dimension => (
              <option key={dimension} value={dimension}>
                Split by {DIMENSION_LABELS[dimension].toLowerCase()}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowTable(value => !value)}
            aria-pressed={showTable}
            className="rounded-xl border border-white/10 px-3 py-1.5 text-xs font-semibold text-gray-300 hover:bg-white/10 hover:text-white"
          >
            {showTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {layers.length > 1 && !showTable && (
        <ul className="mb-2 flex flex-wrap gap-x-4 gap-y-1" aria-label="Legend">
          {layers.map(layer => (
            <li key={layer.key} className="flex items-center gap-1.5 text-xs text-gray-300">
              <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: layer.color }} />
              {layer.label}
            </li>
          ))}
        </ul>
      )}

      <div style={{ opacity: isRefetching ? 0.5 : 1, transition: 'opacity 150ms' }}>
        {isError ? (
          <p className="rounded-2xl border border-rose-400/20 bg-rose-500/[0.06] p-4 text-sm text-rose-200">
            Could not load activity. The event table below is unaffected.
          </p>
        ) : isLoading && !data ? (
          <div className="animate-pulse rounded-2xl bg-white/[0.03]" style={{ height: svgHeight }} aria-hidden />
        ) : showTable ? (
          <ActivityTable buckets={buckets} layers={layers} totals={totals} isSplit={layers.length > 1} />
        ) : (
          <div ref={containerRef} className="relative" onPointerLeave={() => setActive(null)}>
            {isEmpty && (
              <p className="pointer-events-none absolute inset-x-0 top-1/3 text-center text-sm text-gray-500">
                No {metric} in this range
              </p>
            )}
            {width > 0 && (
              <svg
                width={width}
                height={svgHeight}
                role="group"
                aria-label={`${caption}. Use the arrow keys to read each ${data?.unit ?? 'bucket'}.`}
                tabIndex={0}
                onKeyDown={onKeyDown}
                onFocus={() => setActive(current => current ?? buckets.length - 1)}
                onBlur={() => setActive(null)}
                className="block overflow-visible outline-none focus-visible:ring-1 focus-visible:ring-indigo-400/50"
              >
                {/* No data, no scale: an empty chart shows only its baseline. */}
                {!isEmpty && ticks.map(tick => {
                  const y = baselineY - scale(tick)
                  return (
                    <g key={tick}>
                      {tick > 0 && <line x1={LEFT_GUTTER} x2={width - RIGHT_PAD} y1={y} y2={y} stroke={GRID_COLOR} strokeWidth={1} />}
                      <text x={LEFT_GUTTER - 8} y={y} dy="0.32em" textAnchor="end" fontSize={10} fill={MUTED_INK} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {tick.toLocaleString()}
                      </text>
                    </g>
                  )
                })}
                <line x1={LEFT_GUTTER} x2={width - RIGHT_PAD} y1={baselineY} y2={baselineY} stroke={BASELINE_COLOR} strokeWidth={1} />

                {buckets.map((bucket, index) => {
                  const x = LEFT_GUTTER + index * band + (band - barWidth) / 2
                  const segments = layers
                    .map(layer => ({ layer, value: layer.values[index] ?? 0 }))
                    .filter(segment => segment.value > 0)
                  let y = baselineY
                  const dimmed = active !== null && active !== index
                  return (
                    <g key={bucket} opacity={dimmed ? 0.45 : 1}>
                      {segments.map((segment, segmentIndex) => {
                        const isTop = segmentIndex === segments.length - 1
                        const fullHeight = scale(segment.value)
                        // Every segment below the top gives up GAP px of its top edge
                        // to the surface gap; keep at least 1px so it stays visible.
                        const height = isTop ? fullHeight : Math.max(1, fullHeight - GAP)
                        const top = y - fullHeight
                        y = top
                        return isTop ? (
                          <path key={segment.layer.key} d={roundedTopPath(x, top, barWidth, height, RADIUS)} fill={segment.layer.color} />
                        ) : (
                          <rect key={segment.layer.key} x={x} y={top + (fullHeight - height)} width={barWidth} height={height} fill={segment.layer.color} />
                        )
                      })}
                      {labelled.has(index) && (
                        <text x={LEFT_GUTTER + index * band + band / 2} y={baselineY + 16} textAnchor="middle" fontSize={10} fill={MUTED_INK}>
                          {axisLabel(bucket, index === firstLabelled)}
                        </text>
                      )}
                      {index === peakIndex && dataMax > 0 && buckets.length > 1 && (
                        <text x={LEFT_GUTTER + index * band + band / 2} y={baselineY - scale(dataMax) - 5} textAnchor="middle" fontSize={10} fill="#c3c2b7">
                          {dataMax.toLocaleString()}
                        </text>
                      )}
                      {/* The whole column band is the hit target, not just the painted bar. */}
                      <rect
                        x={LEFT_GUTTER + index * band}
                        y={TOP_PAD}
                        width={band}
                        height={PLOT_HEIGHT}
                        fill="transparent"
                        onPointerEnter={() => setActive(index)}
                      />
                    </g>
                  )
                })}
              </svg>
            )}
            {active !== null && buckets[active] !== undefined && width > 0 && (
              <ActivityTooltip
                title={tooltipTitle(buckets[active])}
                layers={layers}
                index={active}
                total={totals[active]}
                isSplit={layers.length > 1}
                metricLabel={metricLabel}
                columnLeft={LEFT_GUTTER + active * band}
                columnRight={LEFT_GUTTER + (active + 1) * band}
                containerWidth={width}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ActivityTooltip({
  title,
  layers,
  index,
  total,
  isSplit,
  metricLabel,
  columnLeft,
  columnRight,
  containerWidth,
}: {
  title: string
  layers: Layer[]
  index: number
  total: number
  isSplit: boolean
  metricLabel: string
  columnLeft: number
  columnRight: number
  containerWidth: number
}) {
  const TOOLTIP_WIDTH = 200
  const OFFSET = 8
  // Beside the column, never over it: the tooltip must not hide the bar it
  // describes. Right of it when there is room, otherwise left.
  const fitsRight = columnRight + OFFSET + TOOLTIP_WIDTH <= containerWidth
  const x = fitsRight ? columnRight + OFFSET : Math.max(0, columnLeft - OFFSET - TOOLTIP_WIDTH)
  // Listed top-down, the way the stack reads.
  const rows = [...layers].reverse()
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-0 z-10 rounded-xl border border-white/10 bg-[#0b0b12]/95 p-3 text-xs shadow-2xl"
      style={{ left: x, width: TOOLTIP_WIDTH }}
    >
      <p className="mb-2 text-gray-400">{title}</p>
      {isSplit && (
        <p className="mb-1.5 flex items-baseline justify-between gap-3">
          <span className="text-gray-400">Total</span>
          <span className="font-semibold tabular-nums text-white">{total.toLocaleString()}</span>
        </p>
      )}
      <ul className="space-y-1">
        {rows.map(layer => (
          <li key={layer.key} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span aria-hidden className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: layer.color }} />
              <span className="truncate text-gray-400">{isSplit ? layer.label : metricLabel}</span>
            </span>
            <span className="font-semibold tabular-nums text-white">{(layer.values[index] ?? 0).toLocaleString()}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ActivityTable({ buckets, layers, totals, isSplit }: { buckets: string[]; layers: Layer[]; totals: number[]; isSplit: boolean }) {
  return (
    <div className="max-h-80 overflow-auto rounded-2xl border border-white/10 custom-scrollbar">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 bg-[#0b0b12]">
          <tr className="text-left text-gray-500">
            <th scope="col" className="px-3 py-2 font-medium">Period</th>
            {layers.map(layer => (
              <th key={layer.key} scope="col" className="px-3 py-2 text-right font-medium">
                {layer.label}
              </th>
            ))}
            {isSplit && <th scope="col" className="px-3 py-2 text-right font-medium">Total</th>}
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket, index) => (
            <tr key={bucket} className="border-t border-white/[0.06] text-gray-300">
              <th scope="row" className="px-3 py-1.5 text-left font-normal">{tooltipTitle(bucket)}</th>
              {layers.map(layer => (
                <td key={layer.key} className="px-3 py-1.5 text-right tabular-nums">{(layer.values[index] ?? 0).toLocaleString()}</td>
              ))}
              {isSplit && <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-white">{totals[index].toLocaleString()}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
